import React, { useState } from "react";
import styled from "styled-components";
import { Button as MuiButton, IconButton } from "@material-ui/core";
import MenuIcon from "@material-ui/icons/Menu";
import CloseIcon from "@material-ui/icons/Close";
import KeyboardArrowDownIcon from "@material-ui/icons/KeyboardArrowDown";
import NotifyModal from "../notify-modal/notify-modal";
import { Link, useHistory, useLocation } from "react-router-dom";
import { frontEnd } from "../../config/links";
import AuthModalContainer from "./auth-modal-container";
import { useUserContext } from "../../context/user-context";

// Define props interface
interface HeaderProps {
  onLogin?: () => void;
  onRegister?: () => void;
}

const HeaderContainer = styled.header`
  background: transparent;
  touch-action: manipulation;
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 1001;
  width: 100%;
  padding: 0 16px;
  border-bottom: 1px solid rgba(203, 215, 244, 0.3);
  backdrop-filter: blur(8px);
  background-color: rgba(255, 255, 255, 0.9);

  @media (min-width: 1440px) {
    padding-left: calc((100vw - 1440px) / 2);
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  max-width: 1384px;
  width: 100%;
  padding: 15px 0;
  box-sizing: border-box; /* This ensures padding is included in width calculation */
  @media (min-width: 768px) {
    padding: 8px 0;
  }
`;

const MainContent = styled.div`
  padding-top: 72px; /* Add padding to account for fixed header height */
`;

export const Logo = styled.img`
  height: 34px;
  margin-right: 12px;
  background-color: transparent;

  @media (max-width: 768px) {
    margin-right: 10px;
    height: 27px;
  }
`;

const LogoContainer = styled.a`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const LogoText = styled.span`
  font-size: 18px;
  font-weight: 800;
  color: #3f5fbf;
`;

const Nav = styled.nav`
  display: flex;
  gap: 48px;
  align-items: center;

  @media (max-width: 768px) {
    display: none;
  }
`;

const MobileNav = styled.nav<{ isOpen: boolean }>`
  display: none;

  @media (max-width: 768px) {
    display: ${({ isOpen }) => (isOpen ? "flex" : "none")};
    position: fixed;
    top: 72px;
    left: 0;
    right: 0;
    background: white;
    flex-direction: column;
    padding: 24px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    touch-action: manipulation;
    z-index: 1000;
    gap: 24px;
  }
`;

const NavLink = styled.a`
  text-decoration: none;
  color: #1c1c1f;
  font-size: 16px;
  display: flex;
  align-items: center;
  font-weight: 700;
  gap: 4px;
`;

const MobileNavLink = styled(NavLink)`
  font-size: 18px;
  font-weight: 600;
  color: #1c1c1f;

  &.sub-item {
    font-size: 16px;
    color: #666;
    padding-left: 16px;
    display: flex;
    flex-direction: column;
    align-items: start;

    .main-text {
      display: flex;
      align-items: center;
      gap: 8px;
    }

    .subtitle {
      display: block;
      font-size: 14px;
      color: #666;
      font-weight: normal;
      margin-top: 4px;
    }
  }
`;

const ButtonGroup = styled.div`
  display: flex;
  gap: 16px;

  @media (max-width: 768px) {
    display: none; /* Hide the entire ButtonGroup on mobile */
  }
`;

export const StyledButton = styled(MuiButton)<{
  buttonVariant?: "primary" | "secondary" | "tertiary";
}>`
  && {
    text-transform: none;
    padding: 12px 16px;
    border-radius: 16px;
    font-size: 16px;

    ${({ buttonVariant }) => {
      switch (buttonVariant) {
        case "primary":
          return `
            background-color: #6B46FF;
            color: white;
            &:hover {
              background-color: #5535CC;
            }
          `;
        case "secondary":
          return `
            background-color: #E5DEFF;
            color: #6B46FF;
            &:hover {
              background-color: #D1C4FF;
            }
          `;
        case "tertiary":
          return `
            background-color: transparent;
            color: #6B46FF;
            border: 1px solid #6B46FF;
            &:hover {
              background-color: rgba(107, 70, 255, 0.04);
            }
          `;
        default:
          return `
            background-color: transparent;
            color: #333;
            &:hover {
              background-color: rgba(107, 70, 255, 0.04);
            }
          `;
      }
    }}
  }
`;

const DropdownMenu = styled.div`
  display: none;
  position: absolute;
  top: calc(100% - 5px); /* Move up slightly to remove gap */
  left: 0;
  background: white;
  flex-direction: column;
  min-width: 250px;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
  border-radius: 4px;
  padding: 8px 0;
  margin-top: 8px;

  &:before {
    content: "";
    position: absolute;
    top: -15px; /* Creates invisible bridge between nav item and dropdown */
    left: 0;
    right: 0;
    height: 20px;
    background: transparent;
  }
`;

const NavItem = styled.div`
  position: relative;
  display: flex;
  align-items: center;
  gap: 4px;
  cursor: pointer;

  &:hover ${DropdownMenu} {
    display: flex;
  }
`;

const DropdownItem = styled.a`
  text-decoration: none;
  color: #333;
  padding: 12px 16px;
  display: flex;
  flex-direction: column;
  gap: 4px;
  font-size: 16px;
  &:hover {
    background: #f5f5f5;
  }
`;

const SubText = styled.span`
  font-size: 14px;
  color: #8d899e;
`;

const Badge = styled.span`
  background: #e2e8f0;
  padding: 2px 6px;
  border-radius: 12px;
  font-size: 12px;
  color: #8d899e;
  max-width: 100px;
  text-align: center;
  margin-left: 0px;
  margin-top: -40px;
`;

const MobileMenuButton = styled(IconButton)`
  && {
    display: none;
    padding: 0;
    min-height: 40px;
    width: 40px;
    @media (max-width: 768px) {
      display: flex;
    }
  }
`;

const MobileMenuContainer = styled.div`
  display: none;
  align-items: center;
  gap: 16px;

  @media (max-width: 768px) {
    display: flex;
  }
`;

const Header: React.FC<HeaderProps> = ({ onLogin, onRegister }) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedExam, setSelectedExam] = useState<string>("");
  const [showAuthModal, setShowAuthModal] = useState(false);

  const { userState } = useUserContext();
  const history = useHistory();
  const location = useLocation();

  console.log("location", location);

  const authPaths = [
    frontEnd.login,
    frontEnd.register,
    frontEnd.registerSuccess,
    frontEnd.confirmEmail,
    frontEnd.resendConfirmEmail,
    frontEnd.closedForAlpha,
    frontEnd.resetPassword,
    frontEnd.forgotPassword
  ];

  // Check if the current path is an auth page (either direct or exam-prefixed)
  const isAuthPage = (() => {
    // Check direct auth paths
    if (authPaths.includes(location.pathname)) {
      return true;
    }

    // Check exam-prefixed auth paths using regex
    const examPrefix = /^\/(bps|gsse|gpepau|gpepnz)/;
    if (examPrefix.test(location.pathname)) {
      // Extract the path without the exam prefix
      const path = location.pathname.replace(examPrefix, "");
      return authPaths.includes(path);
    }

    return false;
  })();

  // Check if current location is an exam page
  const currentExam = (() => {
    const examNames = ["bps", "gsse", "gpepau", "gpepnz"];
    const path = location.pathname.split("/");
    if (path.length > 1 && examNames.includes(path[1])) {
      return path[1];
    }
    return "";
  })();

  const isExamPage = currentExam !== "";

  const showAuth = isAuthPage;

  const handleOpenModal = (examName: string) => {
    setSelectedExam(examName);
    setIsModalOpen(true);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
  };

  const handleEmailSubmit = (email: string) => {
    // Handle the email submission logic here
    // TODOMULTIEXAM
    console.log("Email submitted:", email);
  };

  const navigateToSection = (sectionId: string) => {
    // Check if we're already on the homepage
    if (window.location.pathname === "/" || window.location.pathname === "") {
      // If on homepage, just scroll to the section
      const section = document.getElementById(sectionId);
      if (section) {
        section.scrollIntoView({ behavior: "smooth" });
      }
    } else {
      // If on another page, navigate to homepage with the section hash
      window.location.href = `/#${sectionId}`;
    }
  };

  const handleLoginClick = () => {
    // Use the provided onLogin prop if available, otherwise use default behavior
    if (onLogin) {
      onLogin();
    } else if (isExamPage) {
      history.push(`/${currentExam}${frontEnd.login}`);
    } else {
      history.push(frontEnd.login);
    }
  };

  const handleLoggedInClick = () => {
    history.push(frontEnd.dashboardHome);
  };

  const handleCloseAuthModal = () => {
    // If we're on an exam-specific page, go back to that exam page
    const examMatch = location.pathname.match(/^\/([^/]+)\//);
    if (
      examMatch &&
      ["bps", "gsse", "gpepau", "gpepnz"].includes(examMatch[1])
    ) {
      history.push(`/${examMatch[1]}`);
    } else {
      history.push("/");
    }
  };

  const handleTryForFreeClick = () => {
    // Use the provided onRegister prop if available, otherwise use default behavior
    if (onRegister) {
      onRegister();
    } else if (isExamPage) {
      history.push(`/${currentExam}${frontEnd.register}`);
    } else {
      history.push(frontEnd.register);
    }
  };

  const isLoggedin =
    userState?.attributes?.email && userState?.attributes?.email !== "";

  return (
    <>
      <HeaderContainer>
        <HeaderContent>
          <LogoContainer href="/">
            <Logo src="/assets/home/logo.png" alt="GetThru" />
            <LogoText>GetThru</LogoText>
          </LogoContainer>

          <Nav>
            <NavItem>
              <NavLink href="#exams">
                Exams
                <KeyboardArrowDownIcon style={{ fontSize: 20 }} />
              </NavLink>
              <DropdownMenu>
                <DropdownItem href="/gsse">
                  GSSE
                  <SubText>Generic Surgical Sciences Examination</SubText>
                </DropdownItem>
                <DropdownItem href="/bps">
                  BPS
                  <SubText>Basic Pathological Sciences</SubText>
                </DropdownItem>
                <DropdownItem
                  href="#"
                  onClick={e => {
                    e.preventDefault();
                    handleOpenModal(
                      "General Practice Education Programme (GPEP NZ)"
                    );
                  }}
                >
                  <div>
                    GPEP NZ <Badge>Coming Soon</Badge>
                  </div>
                  <SubText>General Practice Education Programme</SubText>
                </DropdownItem>
                <DropdownItem
                  href="#"
                  onClick={e => {
                    e.preventDefault();
                    handleOpenModal(
                      "General Practice Education Programme (GPEP AU)"
                    );
                  }}
                >
                  <div>
                    GPEP AU <Badge>Coming Soon</Badge>
                  </div>
                  <SubText>General Practice Education Programme</SubText>
                </DropdownItem>
              </DropdownMenu>
            </NavItem>
            <NavLink
              href="/#why-getthru"
              onClick={e => {
                e.preventDefault();
                navigateToSection("why-getthru");
              }}
            >
              Why GetThru?
            </NavLink>
            <NavLink
              href="/#pricing"
              onClick={e => {
                e.preventDefault();
                navigateToSection("pricing");
              }}
            >
              Pricing
            </NavLink>
          </Nav>

          <ButtonGroup>
            {!isLoggedin ? (
              <>
                <StyledButton
                  buttonVariant="tertiary"
                  onClick={handleLoginClick}
                >
                  Log In
                </StyledButton>
                <StyledButton
                  buttonVariant="primary"
                  onClick={handleTryForFreeClick}
                >
                  Try For Free
                </StyledButton>
              </>
            ) : (
              <StyledButton
                buttonVariant="tertiary"
                onClick={handleLoggedInClick}
              >
                Go to dashboard
              </StyledButton>
            )}
          </ButtonGroup>

          <MobileMenuContainer>
            {!isLoggedin ? (
              <StyledButton buttonVariant="tertiary" onClick={handleLoginClick}>
                Log In
              </StyledButton>
            ) : (
              <StyledButton
                buttonVariant="tertiary"
                onClick={handleLoggedInClick}
              >
                Go to dashboard
              </StyledButton>
            )}
            <MobileMenuButton
              onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            >
              {isMobileMenuOpen ? (
                <CloseIcon style={{ height: 40 }} />
              ) : (
                <MenuIcon style={{ height: 40 }} />
              )}
            </MobileMenuButton>
          </MobileMenuContainer>
        </HeaderContent>
      </HeaderContainer>
      <MainContent>
        <MobileNav isOpen={isMobileMenuOpen}>
          <MobileNavLink href="#exams">Exams</MobileNavLink>
          <MobileNavLink href="/gsse" className="sub-item">
            <div className="main-text">GSSE</div>
            <span className="subtitle">
              Generic Surgical Sciences Examination
            </span>
          </MobileNavLink>
          <MobileNavLink href="/bps" className="sub-item">
            <div className="main-text">BPS</div>
            <span className="subtitle">Basic Pathological Sciences</span>
          </MobileNavLink>
          <MobileNavLink
            href="#"
            className="sub-item"
            onClick={e => {
              e.preventDefault();
              handleOpenModal("General Practice Education Programme (GPEP NZ)");
              setIsMobileMenuOpen(false);
            }}
          >
            <div className="main-text">
              GPEP NZ <Badge> Coming Soon</Badge>
            </div>
            <span className="subtitle">
              General Practice Education Programme
            </span>
          </MobileNavLink>
          <MobileNavLink
            href="#"
            className="sub-item"
            onClick={e => {
              e.preventDefault();
              handleOpenModal("General Practice Education Programme (GPEP AU)");
              setIsMobileMenuOpen(false);
            }}
          >
            <div className="main-text">
              GPEP AU <Badge>Coming Soon</Badge>
            </div>
            <span className="subtitle">
              General Practice Education Programme
            </span>
          </MobileNavLink>
          <MobileNavLink
            href="/#why-getthru"
            onClick={e => {
              e.preventDefault();
              navigateToSection("why-getthru");
              setIsMobileMenuOpen(false); // Close mobile menu after navigation
            }}
          >
            Why GetThru?
          </MobileNavLink>
          <MobileNavLink
            href="/#pricing"
            onClick={e => {
              e.preventDefault();
              navigateToSection("pricing");
              setIsMobileMenuOpen(false); // Close mobile menu after navigation
            }}
          >
            Pricing
          </MobileNavLink>
          {!isLoggedin ? (
            <StyledButton
              buttonVariant="tertiary"
              fullWidth
              onClick={handleLoginClick}
            >
              Log In
            </StyledButton>
          ) : (
            <StyledButton
              buttonVariant="tertiary"
              fullWidth
              onClick={handleLoggedInClick}
            >
              Go to dashboard
            </StyledButton>
          )}
          <StyledButton
            buttonVariant="primary"
            fullWidth
            onClick={handleTryForFreeClick}
            style={{ marginTop: 16 }}
          >
            Try For Free
          </StyledButton>
        </MobileNav>
      </MainContent>
      <NotifyModal
        isOpen={isModalOpen}
        onClose={handleCloseModal}
        onSubmit={handleEmailSubmit}
        examName={selectedExam}
      />

      {/* Conditionally render the auth modal */}
      {showAuth && <AuthModalContainer onClose={handleCloseAuthModal} />}
    </>
  );
};

export default Header;
